import { Link } from 'react-router-dom'
import { ArrowRight, Sparkles, Users, Film, BarChart3, Coins } from 'lucide-react'
import { Button } from '@/components/ui/button'

const benefits = [
  {
    icon: Film,
    title: 'Built for series, not skits',
    body: 'Craft real shows with episodes, seasons, and characters – not one-off clips that vanish in a feed.',
  },
  {
    icon: BarChart3,
    title: 'Insights that matter',
    body: "See who's watching, where they drop off, and which episodes hit hardest – so you can write the next one smarter.",
  },
  {
    icon: Coins,
    title: 'Aligned with your upside',
    body: 'As monetisation rolls out, Binyuma is built to share value with the people actually making the stories.',
  },
]

export function Creators() {
  return (
    <div className="min-h-screen bg-black text-white">
      <section className="relative max-w-6xl mx-auto px-6 py-16">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-black to-black -z-10" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_0,rgba(212,175,55,0.18),transparent_55%),radial-gradient(circle_at_100%_100%,rgba(215,38,56,0.35),transparent_55%)] -z-10" />

        <div className="flex items-center justify-between gap-4 mb-10">
          <Link to="/" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors">
            &larr; Back to home
          </Link>
          <span className="inline-flex items-center gap-2 text-xs md:text-sm text-gray-400">
            <Users className="w-4 h-4 text-[#D4AF37]" />
            For African filmmakers & storytellers
          </span>
        </div>

        <div className="grid md:grid-cols-[1.2fr_minmax(0,0.9fr)] gap-10 md:gap-14 items-start mb-16">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs font-medium text-[#FFD700] mb-4">
              <Sparkles className="w-4 h-4" />
              Your series deserves more than a feed
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
              <span className="bg-gradient-to-r from-[#D4AF37] via-[#FFFFFF] to-[#D4AF37] bg-clip-text text-transparent">
                Create on Binyuma TV
              </span>
            </h1>
            <p className="text-base md:text-lg text-gray-300 mb-6 max-w-xl">
              Binyuma is the vertical‑first home for African creators who think in seasons, not skits. Turn your
              stories into full‑screen series watched across Africa and the diaspora.
            </p>
            <p className="text-sm md:text-base text-gray-400 mb-8 max-w-xl">
              With Binyuma Studio on web and the Binyuma TV app in viewers' hands, you get a direct line from
              your camera roll to a global audience – without begging a generic algorithm.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <Link to="/creator" className="inline-block">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-[#D72638] hover:bg-[#D72638]/90 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-[0_0_40px_rgba(215,38,56,0.65)]"
                >
                  Open creator studio
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/signup" className="inline-block">
                <Button
                  size="lg"
                  variant="secondary"
                  className="w-full sm:w-auto border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-6 text-lg font-semibold rounded-xl"
                >
                  Sign up
                </Button>
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute -inset-6 bg-gradient-to-tr from-[#D72638]/40 via-transparent to-[#FFD700]/40 blur-3xl opacity-70" />
            <div className="relative rounded-[32px] border border-white/10 bg-gradient-to-b from-[#1b1026] via-[#050309] to-black p-[10px] shadow-[0_20px_60px_rgba(0,0,0,0.7)]">
              <div className="rounded-[26px] bg-black overflow-hidden">
                <div className="h-10 flex items-center justify-between px-4 border-b border-white/5 bg-gradient-to-r from-black to-[#120817]">
                  <span className="text-xs text-gray-400">Binyuma Studio</span>
                  <span className="text-[10px] text-gray-500">CREATOR VIEW</span>
                </div>
                <div className="aspect-[1/2] bg-gradient-to-b from-[#1B1038] via-[#1E193D] to-black flex flex-col justify-between p-4">
                  <div className="flex justify-between items-start gap-3">
                    <div className="rounded-full bg-black/40 px-3 py-1 text-[10px] uppercase tracking-wide text-gray-200 border border-white/10">
                      New episode uploaded
                    </div>
                    <div className="flex flex-col gap-1 items-end text-[10px] text-gray-300">
                      <span>Watch time ↑ 34%</span>
                      <span className="text-gray-400">Top city: Lagos</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">From idea to season one.</h3>
                    <p className="text-xs text-gray-300 mb-4 line-clamp-3">
                      Track how viewers binge your series, see where they drop off, and plan the next episode with
                      real data from real fans.
                    </p>
                    <div className="flex items-center justify-between text-[10px] text-gray-300">
                      <span className="inline-flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        12.4k followers
                      </span>
                      <span className="px-2 py-1 rounded-full bg-white/10 border border-white/20">
                        Creator • Verified
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-6 border-t border-gray-900 pt-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">Why creators choose Binyuma</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b) => (
              <div
                key={b.title}
                className="p-6 rounded-xl bg-gradient-to-br from-gray-900 to-black border border-gray-800"
              >
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mb-4">
                  <b.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{b.title}</h3>
                <p className="text-sm text-gray-400">{b.body}</p>
              </div>
            ))}
          </div>
        </section>
      </section>
    </div>
  )
}
